import posthog from './posthog';

interface FeatureFlagState {
  enabled: boolean;
  isLoading: boolean;
}

const flagCache = new Map<string, FeatureFlagState>();
let fallbackReady = false;
let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

function getTestFlagState(flagName: string): FeatureFlagState {
  const cached = flagCache.get(flagName);
  if (cached) {
    return cached;
  }
  const state = { enabled: true, isLoading: false };
  flagCache.set(flagName, state);
  return state;
}

// `posthog.__loaded` only means init() ran; flags arrive asynchronously via
// remote config, so `hasLoadedFlags` is the correct readiness signal.
function flagsHaveLoaded(): boolean {
  return !!posthog.featureFlags?.hasLoadedFlags;
}

export function getFeatureFlagSnapshot(flagName: string): FeatureFlagState {
  if (import.meta.env.MODE === 'test') {
    return getTestFlagState(flagName);
  }

  const loaded = flagsHaveLoaded();
  if (loaded) {
    const enabled = !!posthog.isFeatureEnabled(flagName);
    const cached = flagCache.get(flagName);
    if (cached && cached.enabled === enabled && cached.isLoading === false) {
      return cached;
    }
    const state = { enabled, isLoading: false };
    flagCache.set(flagName, state);
    return state;
  }

  if (fallbackReady) {
    const cached = flagCache.get(flagName);
    if (cached && cached.enabled === false && cached.isLoading === false) {
      return cached;
    }
    const state = { enabled: false, isLoading: false };
    flagCache.set(flagName, state);
    return state;
  }

  const cached = flagCache.get(flagName);
  if (cached && cached.enabled === false && cached.isLoading === true) {
    return cached;
  }
  const state = { enabled: false, isLoading: true };
  flagCache.set(flagName, state);
  return state;
}

export function subscribeToFeatureFlags(callback: () => void): () => void {
  const unsubscribe =
    posthog.onFeatureFlags?.(() => {
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      callback();
    }) ?? (() => {});

  if (!flagsHaveLoaded() && !fallbackReady && !fallbackTimer) {
    fallbackTimer = setTimeout(() => {
      fallbackReady = true;
      fallbackTimer = null;
      callback();
    }, 3000);
  }

  return () => {
    unsubscribe();
    if (fallbackTimer) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  };
}
